/**
 * What every Atlas tab shares: the design's role names and colours (atlasChat L31199),
 * its mark style, its clock format, and the console width the design lays out against.
 */
import { useEffect, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { pathForView, type AtlasTone, type Role, type ViewId } from '@aithora/shared';

/** The design's Atlas roles (atlasVals TAB_ROLES, L32031). */
export type AtlasRole = 'Care Staff' | 'Facility Admin' | 'Operator' | 'Partner' | 'Developer' | 'Family';

export function atlasRoleOf(role: Role | null): AtlasRole {
  if (role === 'CAREGIVER' || role === 'STAFF') return 'Care Staff';
  if (role === 'ADMIN') return 'Facility Admin';
  if (role === 'PARTNER') return 'Partner';
  // Production's Developer lens: the Atlas assistant and its Eval, no resident PHI and no
  // governance (Andrew, 2026-09-30: Developers keep Atlas, scoped as today).
  if (role === 'DEVELOPER') return 'Developer';
  // Family: Chat only, about their own person (Andrew, 2026-10-19).
  if (role === 'FAMILY_MEMBER') return 'Family';
  return 'Operator';
}

export const ATLAS_TABS = ['Chat', 'Conversations', 'Scribe', 'Actions', 'Autonomy', 'Eval'] as const;
export type AtlasTab = (typeof ATLAS_TABS)[number];

/** Which roles open each tab (design TAB_ROLES): the others are absent, not emptied. */
const TAB_ROLES: Record<AtlasTab, AtlasRole[] | null> = {
  Chat: null,
  // V6: staff read residents' conversations here (Atlas is retired).
  Conversations: ['Care Staff', 'Facility Admin', 'Operator', 'Partner'],
  Scribe: ['Care Staff', 'Facility Admin', 'Operator'],
  Actions: ['Facility Admin', 'Operator'],
  Autonomy: ['Facility Admin', 'Operator'],
  Eval: ['Operator', 'Developer'],
};
export const tabAllowed = (t: AtlasTab, r: AtlasRole) => !TAB_ROLES[t] || TAB_ROLES[t]!.includes(r);

export const INK = '#22272E';
export const MUTE = '#54606C';
export const DIM = '#54606C'; // V9 contrast: 4.5:1 on cream (the design's #8B94A0 read 2.9:1)
export const BLUE = '#2F5C97';
export const RED = '#B3261E';
export const AMBER = '#4A5866';
export const OK = '#1F6B52';
export const VIO = '#5B6B7C';
export const TONE: Record<AtlasTone, string> = { '': INK, red: RED, amber: AMBER, ok: OK, mute: MUTE, blue: BLUE, vio: VIO };
export const EASE = 'cubic-bezier(.22,.61,.36,1)';

/** The design's mark: a 16px tag, the tone on a 10% tint of itself (atlasChat mark()). */
export const mark = (tone: AtlasTone | string) => {
  const c = TONE[tone as AtlasTone] ?? MUTE;
  return 'flex:0 0 auto;display:inline-flex;align-items:center;height:22px;padding:0 6px;border-radius:3px;font-size:14px;font-weight:700;letter-spacing:.06em;white-space:nowrap;color:' + c + ';background:' + c + '1A;';
};

/** The design's live clock (atlasChat clock()): '2h 14m', or '4m 12s' under an hour. */
export function clockText(seconds: number): string {
  const t = Math.max(0, Math.floor(seconds));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m ${t % 60}s`;
}

/** Whole seconds since an instant, on the page's clock. */
export const secondsSince = (iso: string, now: number) => (now - Date.parse(iso)) / 1000;

/** A clock that re-renders every second (the design's `tick`). */
export function useNow(ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
}

/** The console's width, which the design lays the chat out against (console-root). */
export function useShellWidth(): number {
  const [w, setW] = useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth));
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return w;
}

export const stop = (e?: MouseEvent | { preventDefault?: () => void }) => {
  if (e && e.preventDefault) e.preventDefault();
};

/** Open a console page (the design's go(view)). */
export function useGo() {
  const navigate = useNavigate();
  return (view: ViewId | null) => (e?: MouseEvent) => {
    stop(e);
    if (view) navigate(pathForView(view));
  };
}

const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
/** 'one', 'two' ... up to ten, then the figure (the design writes small counts in words). */
export const inWords = (n: number) => (n >= 0 && n <= 10 ? WORDS[n] : String(n));

/** The console never shows the long dash (code point 8212): a served sentence carrying one gets a colon instead. */
const LONG_DASH = new RegExp('\\s*' + String.fromCharCode(8212) + '\\s*', 'g');
export const noDash = (s: string) => s.replace(LONG_DASH, ': ');
